import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { KeyRound, ArrowLeft, ShieldAlert } from 'lucide-react';

// Step 2: the screen the emailed link opens. The token is checked before the
// form is shown, so an expired link says so immediately instead of after the
// user has typed a new password twice.
export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [owner, setOwner] = useState(null);   // { name, email } when the link is good
  const [dead, setDead] = useState('');       // why the link cannot be used
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`/crm-api/auth/reset-password/${encodeURIComponent(token)}`);
        const d = await r.json();
        if (!alive) return;
        if (d.success) setOwner({ name: d.name, email: d.email });
        else setDead(d.error || 'This reset link is no longer valid.');
      } catch {
        if (alive) setDead('Cannot reach the server. Check your connection and try again.');
      } finally {
        if (alive) setChecking(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (pw !== pw2) { setError('The two passwords do not match'); return; }
    setBusy(true);
    try {
      const r = await fetch('/crm-api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: pw }),
      });
      const d = await r.json();
      if (!d.success) { setError(d.error || 'Could not reset your password.'); return; }

      // The server signs them in as part of the reset, so land them in the right
      // area rather than bouncing back to a login form.
      localStorage.setItem('crm_token', d.token);
      localStorage.setItem('crm_user', JSON.stringify(d.user));
      if (d.user.role === 'admin') {
        localStorage.setItem('token', d.token);
        navigate('/dashboard');
      } else {
        localStorage.setItem('employee_token', d.token);
        localStorage.setItem('employee_id', d.user.id);
        localStorage.setItem('employee_name', d.user.name);
        localStorage.setItem('employee_slug', d.user.profileSlug);
        navigate('/employee/home');
      }
    } catch {
      setError('Cannot reach the server. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1424] via-[#16294d] to-[#060b16] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-white rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-xl p-2">
            <img src="/cartello-logo.png" alt="Cartello Software Solutions" className="max-w-full max-h-full object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-1">Cartello Software Solutions</h1>
          <p className="text-blue-100 font-medium">Choose a new password</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {checking && <p className="text-sm text-gray-500 text-center py-6">Checking your link…</p>}

          {!checking && dead && (
            <div className="text-center">
              <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-800 mb-2">Link no longer valid</h2>
              <p className="text-sm text-gray-600 leading-relaxed">{dead}</p>
              <Link
                to="/forgot-password"
                className="inline-block mt-6 bg-gradient-to-r from-[#2563eb] to-[#1e40af] text-white px-5 py-2.5 rounded-lg text-sm font-semibold"
              >
                Request a new link
              </Link>
            </div>
          )}

          {!checking && owner && (
            <form onSubmit={submit} className="space-y-6">
              <p className="text-sm text-gray-600">
                Setting a new password for <strong className="text-gray-800">{owner.name}</strong>
                <br />
                <span className="text-gray-500">{owner.email}</span>
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New password</label>
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="At least 6 characters"
                  required
                  autoFocus
                  disabled={busy}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm new password</label>
                <input
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Type it again"
                  required
                  disabled={busy}
                />
              </div>

              <button
                type="submit"
                disabled={busy}
                className="w-full bg-gradient-to-r from-[#2563eb] to-[#1e40af] text-white py-3 rounded-lg font-semibold hover:from-[#1d4ed8] hover:to-[#1e3a8a] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <KeyRound className="w-5 h-5" />
                {busy ? 'Saving…' : 'Save and sign in'}
              </button>

              <div className="text-center">
                <Link to="/employee/login" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#2563eb]">
                  <ArrowLeft className="w-4 h-4" /> Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>

        <div className="text-center mt-6">
          <p className="text-blue-100 text-sm">© 2026 Cartello Software Solutions</p>
        </div>
      </div>
    </div>
  );
}
